import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourse } from '../../context/CourseContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  BookOpen,
  Menu,
  X,
  Bookmark,
  Settings,
  Home,
  List,
  Award,
  Sun,
  Moon,
} from 'lucide-react';

export const Header = () => {
  const location = useLocation();
  const { overallProgress, bookmarks } = useCourse();
  const { isDark, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // On the public welcome page, hide course-specific nav (Chapters/Bookmarks/Progress)
  // since those are only meaningful to existing E-Course buyers.
  const isCourseContext = location.pathname.startsWith('/course')
    || location.pathname.startsWith('/chapters')
    || location.pathname.startsWith('/chapter')
    || location.pathname.startsWith('/bookmarks')
    || location.pathname.startsWith('/progress');

  const navItems = isCourseContext
    ? [
        { path: '/course', label: 'Course Home', icon: Home },
        { path: '/chapters', label: 'Chapters', icon: List },
        { path: '/bookmarks', label: 'Bookmarks', icon: Bookmark, badge: bookmarks.length },
        { path: '/progress', label: 'My Progress', icon: Award },
      ]
    : [
        { path: '/', label: 'Home', icon: Home },
        { path: '/ebook', label: 'E-Book', icon: BookOpen },
        { path: '/collective', label: 'The Collective', icon: Bookmark },
      ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-silver/20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-maroon flex items-center justify-center shadow-glow transition-shadow duration-300 group-hover:shadow-maroon">
                <BookOpen className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-secondary flex items-center justify-center shadow-gold">
                <span className="text-[8px] font-bold text-secondary-foreground">5</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-display text-lg font-semibold text-foreground leading-tight">
                Taking Your Power Back
              </h1>
              <p className="text-xs text-muted-foreground">{isCourseContext ? 'E-Course' : 'Breah Kettunen'}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive(item.path) ? "default" : "ghost"}
                  size="sm"
                  className={`gap-2 relative ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-primary to-maroon text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:text-foreground hover:bg-silver/10'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-secondary text-secondary-foreground text-xs flex items-center justify-center font-medium shadow-gold">
                      {item.badge}
                    </span>
                  )}
                </Button>
              </Link>
            ))}
          </nav>

          {/* Progress Indicator (Desktop) — only on course pages */}
          <div className="hidden lg:flex items-center gap-4">
            {isCourseContext && (
              <div className="flex items-center gap-3">
                <div className="w-32">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium text-maroon">{overallProgress}%</span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </div>
              </div>
            )}
            
            {/* Dark Mode Toggle */}
            <Button 
              variant="outline" 
              size="icon" 
              className="rounded-full border-silver/30 hover:border-silver hover:bg-silver/10"
              onClick={toggleTheme}
              data-testid="theme-toggle-desktop"
            >
              <motion.div
                initial={false}
                animate={{ rotate: isDark ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isDark ? (
                  <Sun className="w-4 h-4 text-secondary" />
                ) : (
                  <Moon className="w-4 h-4 text-maroon" />
                )}
              </motion.div>
            </Button>
          </div>

          {/* Mobile Menu Trigger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 border-l border-silver/20">
              <div className="flex flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="flex items-center justify-between pb-6 border-b border-silver/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-maroon flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h2 className="font-display font-semibold">Your Power</h2>
                      <p className="text-xs text-muted-foreground">E-Course</p>
                    </div>
                  </div>
                </div>

                {/* Progress */}
                <div className="py-6 border-b border-silver/20">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Overall Progress</span>
                    <span className="font-semibold text-maroon">{overallProgress}%</span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-6">
                  <ul className="space-y-2">
                    {navItems.map((item) => (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isActive(item.path)
                              ? 'bg-gradient-to-r from-primary to-maroon text-primary-foreground shadow-glow'
                              : 'hover:bg-silver/10 text-foreground'
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                          {item.badge > 0 && (
                            <span className="ml-auto w-6 h-6 rounded-full bg-secondary text-secondary-foreground text-xs flex items-center justify-center font-medium">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Settings */}
                <div className="pt-6 border-t border-silver/20">
                  <Button 
                    variant="outline" 
                    className="w-full gap-2 border-silver/30 hover:border-silver hover:bg-silver/10"
                    onClick={toggleTheme}
                    data-testid="theme-toggle-mobile"
                  >
                    {isDark ? (
                      <>
                        <Sun className="w-4 h-4 text-secondary" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="w-4 h-4 text-maroon" />
                        Dark Mode
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
